import {Button, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch} from '@material-ui/core';
import {hideModal} from 'Core/ducks/modal';
import {initiateTokenRefresh, toggleRefreshSpinner, TokenRefreshSessionKeys} from 'Core/ducks/refreshToken';
import {setRefreshReRouteOptions} from 'Core/refreshTokenUtilityMethods';
import {identity} from 'fp-ts/lib/function';
import {fromArray} from 'fp-ts/lib/NonEmptyArray';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import Modal from 'Shared/components/Modal';
import withUserClaims, {WithClaimsProps} from 'Shared/components/withUserClaims';
import {isUserEmulating} from 'Shared/utilities/impersonationHelpers';
import {updateDefaultAgencyAssociation} from 'Users/ducks/userAccount';
import {AgentAgencyClaim} from 'Users/models/AgentAgencyClaim';
import './SwitchTaxIdModal.sass';
import {ANALYTICS_EVENT_ACTION} from 'Core/components/Analytics/AnalyticsEventConstants';
import {changeAnalyticsState} from 'Core/ducks/analytics';

type SwitchTaxIdModalProps = {
  agencyAssociations: Array<AgentAgencyClaim>;
  onClose: typeof hideModal;
} & WithClaimsProps;

const SwitchTaxIdModal = (props: SwitchTaxIdModalProps) => {
  const dispatch = useDispatch();
  const {analytics} = useSelector((state: ReducerState) => state.analytics);
  const {selectedAgency, userClaims} = useSelector((state: ReducerState) => state.user.profile);

  const isImpersonating = isUserEmulating(userClaims);

  const currentTaxIdNumber = pipe(
    selectedAgency,
    O.fold(
      () => '',
      agency => agency.encryptedIdentifier
    )
  );
  const currentNyFilteringStatus = pipe(
    selectedAgency,
    O.fold(
      () => false,
      agency => agency.isNewYork || false
    )
  );

  const [showNy, setShowNy] = useState<boolean>(currentNyFilteringStatus || false);
  const [selectedAccount, setAccount] = useState(currentTaxIdNumber);

  const getSelectedAgency = (selectedEncryptedId: string) =>
    props.agencyAssociations.filter(c => c.encryptedIdentifier === selectedEncryptedId)[0];

  const currentSelectedAgency = getSelectedAgency(selectedAccount);
  const hasNyAndNonNyNumbers = pipe(
    currentSelectedAgency,
    O.fromNullable,
    O.fold(
      () => false,
      claims => {
        const {agentNumbers} = claims;
        return agentNumbers.some(agentNum => agentNum.isNewYork) && agentNumbers.some(agentNum => !agentNum.isNewYork);
      }
    )
  );
  const agencyAssociations = props.agencyAssociations.filter(agency => agency.agentNumbers.length > 0);
  const agencyAssociationSelections = pipe(
    agencyAssociations,
    fromArray,
    O.map(associations => {
      return associations.map(claim => (
        <MenuItem key={`agency__${claim.maskedIdentifier}`} value={claim.encryptedIdentifier}>
          {claim.maskedIdentifier}
        </MenuItem>
      ));
    })
  );

  const currentSelectedAccountIsTheDefault =
    currentSelectedAgency.isDefault &&
    (hasNyAndNonNyNumbers ? currentSelectedAgency.isDefaultShowNewYork === showNy : true);
  const [isDefault, setIsDefault] = useState(false);
  useEffect(() => {
    setIsDefault(currentSelectedAccountIsTheDefault);
  }, [currentSelectedAccountIsTheDefault]);

  const isSwitchingAccount =
    currentSelectedAgency.encryptedIdentifier !== currentTaxIdNumber || currentNyFilteringStatus !== showNy;
  const isUpdatingDefaultAccount = isDefault && !currentSelectedAccountIsTheDefault;
  const nyStatusToSave =
    currentSelectedAgency.agentNumbers.length === 0
      ? false
      : hasNyAndNonNyNumbers
      ? showNy
      : currentSelectedAgency.agentNumbers[0].isNewYork;

  return (
    <Modal onClose={props.onClose} title="Switch Accounts" open>
      <div className="legacy-sign-on-form">
        <div>Please Select the Agency Account You Would Like to Use:</div>
        <FormControl className="legacy-sign-on-form__account-select">
          <InputLabel shrink={Boolean(selectedAccount)} htmlFor="account-select">
            {O.isNone(agencyAssociationSelections) ? 'No Accounts Available' : 'Account'}
          </InputLabel>
          <Select
            className="switch-tax-id-modal__tax-id-select"
            value={selectedAccount}
            inputProps={{
              id: 'account-select',
            }}
            disabled={O.isNone(agencyAssociationSelections)}
            onChange={event => {
              setAccount(event.target.value as string);
              setIsDefault(false);
            }}
          >
            {pipe(
              agencyAssociationSelections,
              O.fold(() => [<></>], identity)
            )}
          </Select>
          {hasNyAndNonNyNumbers && (
            <FormControlLabel
              className="switch-tax-id-modal__new-york-switch"
              control={
                <Switch
                  color="primary"
                  checked={showNy}
                  onChange={e => {
                    setShowNy(e.target.checked);
                  }}
                  value={showNy}
                />
              }
              label="View New York?"
            />
          )}
          {!isImpersonating && (
            <FormControlLabel
              control={
                <Switch
                  disabled={currentSelectedAccountIsTheDefault}
                  checked={isDefault}
                  onChange={e => {
                    setIsDefault(e.target.checked);
                  }}
                  value={isDefault}
                  color="primary"
                />
              }
              label={currentSelectedAccountIsTheDefault ? 'Selected Account Is Currently Default' : 'Use As Default'}
            />
          )}
        </FormControl>
        <Button
          className="legacy-sign-on-form__login-button"
          disabled={(!isSwitchingAccount && !isUpdatingDefaultAccount) || O.isNone(agencyAssociationSelections)}
          color="primary"
          variant="contained"
          fullWidth
          onClick={() => {
            if (isUpdatingDefaultAccount) {
              dispatch(
                toggleRefreshSpinner(true, `Updating Default to ${currentSelectedAgency.maskedIdentifier} Account`)
              );
              dispatch(
                updateDefaultAgencyAssociation(
                  dispatch,
                  currentSelectedAgency,
                  props.claims.id,
                  currentSelectedAgency.encryptedIdentifier,
                  hasNyAndNonNyNumbers ? showNy : nyStatusToSave
                )
              );
            }

            if (isSwitchingAccount) {
              dispatch(toggleRefreshSpinner(true, `Switching to ${currentSelectedAgency.maskedIdentifier} Account`));
              localStorage.setItem(TokenRefreshSessionKeys.NEWYORK_STATUS, `${nyStatusToSave}`);
            }
            dispatch(
              changeAnalyticsState({
                page: analytics.page,
                ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.SWITCH_ACCOUNTS_APPLY},
              })
            );
            setRefreshReRouteOptions(false);
            sessionStorage.setItem('savedEncryptedId', currentSelectedAgency.encryptedIdentifier);
            dispatch(initiateTokenRefresh(currentSelectedAgency.maskedIdentifier));

            props.onClose();
          }}
        >
          Apply
        </Button>
      </div>
    </Modal>
  );
};

export default withUserClaims(SwitchTaxIdModal);
